import { Component, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { CreatePromotionalContent, CreatePromotionalContentFactory, PROM_CONTENT_FIELD_LABELS } from "../../models"
import { PromotionalContentService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"

import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"

@Component({
    selector: "valben-promotional-content-add",
    templateUrl: "./promotional-content-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        BooleanInputComponent,
        FileInputComponent,
    ],
})
export class PromotionalContentAddPage {
    private _promContentService = inject(PromotionalContentService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    saving = signal(false)

    model = signal<CreatePromotionalContent>(CreatePromotionalContentFactory.create())
    promContentForm = form(this.model)

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/promotional-content"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.promContentForm.isDraft().value.set(isDraft)
        this.saving.set(true)
        let failed = false

        const created = await submit(this.promContentForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._promContentService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.promContentForm, PROM_CONTENT_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.promContentForm, PROM_CONTENT_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
